import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Canvas signature pad. Emits a PNG Blob whenever the drawing changes.
 *
 * Extracted from TelemedAgreementSign so the onboarding flow and the Settings
 * re-sign flow capture signatures identically — a second hand-rolled pad would
 * drift on stroke width, export format, or touch handling.
 */
interface SignaturePadProps {
  onChange: (blob: Blob | null) => void;
  disabled?: boolean;
  height?: number;
  className?: string;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  onChange,
  disabled = false,
  height = 160,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawingRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctxRef.current = ctx;
  }, []);

  useEffect(() => { initCanvas(); }, [initCanvas]);

  const pointFromEvent = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(clientX - rect.left, canvas.width)),
      y: Math.max(0, Math.min(clientY - rect.top, canvas.height))
    };
  };

  const startAt = (x: number, y: number) => {
    const ctx = ctxRef.current;
    if (!ctx || disabled) return;
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveAt = (x: number, y: number) => {
    const ctx = ctxRef.current;
    if (!ctx || !drawingRef.current) return;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStrokes(true);
  };

  const endStroke = () => {
    const ctx = ctxRef.current;
    if (!ctx || !drawingRef.current) return;
    ctx.closePath();
    drawingRef.current = false;
    canvasRef.current?.toBlob(blob => onChange(blob), 'image/png', 0.8);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
    onChange(null);
  };

  return (
    <div className={className}>
      <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white">
        <canvas
          ref={canvasRef}
          width={600}
          height={height}
          className="w-full touch-none cursor-crosshair rounded-lg"
          style={{ height }}
          onMouseDown={e => { const p = pointFromEvent(e.clientX, e.clientY); startAt(p.x, p.y); }}
          onMouseMove={e => { const p = pointFromEvent(e.clientX, e.clientY); moveAt(p.x, p.y); }}
          onMouseUp={endStroke}
          onMouseLeave={endStroke}
          onTouchStart={e => {
            e.preventDefault();
            const t = e.touches[0];
            const p = pointFromEvent(t.clientX, t.clientY);
            startAt(p.x, p.y);
          }}
          onTouchMove={e => {
            e.preventDefault();
            const t = e.touches[0];
            const p = pointFromEvent(t.clientX, t.clientY);
            moveAt(p.x, p.y);
          }}
          onTouchEnd={e => { e.preventDefault(); endStroke(); }}
        />
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-gray-500">Sign above using your mouse or finger.</p>
        <button
          type="button"
          onClick={clear}
          disabled={!hasStrokes || disabled}
          className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-40"
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
